import {
  canAccessFeature,
  featureForRoutePath,
  FeatureScopes,
} from "./featureScopes";

/**
 * A page a role can be set to open on. Paths are the sidebar destinations, with a
 * leading slash - the same form Betty stores and mints as the defaultRoute claim.
 */
export interface LandingPageOption {
  path: string;
  label: string;
  /** Sidebar name for TME customers where it differs. */
  tmeLabel?: string;
  /** Not on the TME sidebar at all. */
  hideForTme?: boolean;
  /** Only on the TME sidebar. */
  tmeOnly?: boolean;
}

/**
 * Mirrors the sidebar in DashboardLayout. Dashboard ("/") is deliberately absent -
 * "Automatic" already means home, and Betty normalises "/" back to unset.
 * Feature-flag-gated pages are listed regardless: if the flag is off the sidebar
 * item is disabled and resolveLandingPath falls back rather than sending anyone there.
 */
export const LANDING_PAGES: LandingPageOption[] = [
  { path: "/chat", label: "Team Chat" },

  { path: "/quotes", label: "Quotes", hideForTme: true },
  { path: "/leads", label: "Leads", hideForTme: true },
  { path: "/bookings", label: "Bookings", hideForTme: true },
  { path: "/vehicles-collected", label: "Collected", hideForTme: true },
  { path: "/collections-board", label: "Collections Board", hideForTme: true },
  { path: "/trip-manager", label: "Trip Manager", hideForTme: true },
  { path: "/drop-offs", label: "Drop Offs" },
  { path: "/schedule", label: "Schedule", hideForTme: true },
  { path: "/payments", label: "Payments" },

  { path: "/accounts", label: "Accounts" },
  { path: "/invoices", label: "Invoices" },
  { path: "/sales-quotes", label: "Sales Quotes" },

  { path: "/vehicles", label: "Vehicles" },
  { path: "/vehicle-board", label: "Vehicle Board", tmeOnly: true },
  { path: "/parts-manager", label: "Awaiting Inventory", hideForTme: true },
  { path: "/triage", label: "Triage" },
  { path: "/depollution", label: "Depollution", hideForTme: true },
  { path: "/jobs", label: "Jobs", hideForTme: true },
  { path: "/production-planner", label: "Production Planner", tmeLabel: "Dismantling Planner" },
  { path: "/production-dashboard", label: "Production Dashboard", tmeLabel: "Leader Dashboard" },
  { path: "/station-manager", label: "Station Manager" },

  { path: "/work-orders", label: "Work Orders" },
  { path: "/exports", label: "Exports", hideForTme: true },
  { path: "/awaiting-arrival", label: "Awaiting Arrival", hideForTme: true },
  { path: "/parts-checker", label: "Parts Checker", hideForTme: true },
  { path: "/stock", label: "Stock" },
  { path: "/core", label: "RS Core" },
  { path: "/core-bins", label: "Bins" },
  { path: "/stock-qc", label: "Quality Control" },
  { path: "/part-requests", label: "Part Requests" },

  { path: "/ebay-unlisted", label: "Unlisted Parts", hideForTme: true },
  { path: "/recently-listed", label: "Recently Listed", hideForTme: true },
  { path: "/ebay-listings", label: "Active Listings", hideForTme: true },
  { path: "/ebay-optimisation", label: "Listing Optimisation", hideForTme: true },
  { path: "/ebay-orders", label: "Orders" },
  { path: "/ebay-returns", label: "eBay Returns", hideForTme: true },

  { path: "/widget-dashboard", label: "Widget Dashboard", hideForTme: true },
  { path: "/widget-calculator", label: "Widget Calculator", hideForTme: true },
  { path: "/sellavehicle", label: "SellAVehicle", hideForTme: true },
  { path: "/abandoned-quotes", label: "Widget History", hideForTme: true },

  { path: "/reports", label: "Reports" },
  { path: "/performance", label: "Performance Tracking" },

  { path: "/find-lead", label: "Find Lead" },

  { path: "/ebay-messages", label: "eBay Messages" },
  { path: "/partpro-messages", label: "PartPro Messages" },
  { path: "/tickets", label: "Support Tickets" },
  { path: "/admin", label: "Admin Settings" },
];

export function landingPageLabel(option: LandingPageOption, isTme: boolean): string {
  return isTme && option.tmeLabel ? option.tmeLabel : option.label;
}

/**
 * The pages a role with these scopes could open on. Uses the same route → feature
 * mapping as the sidebar gate, so a role is never offered a page it can't reach.
 */
export function landingPageOptionsFor(
  scopes: FeatureScopes | undefined,
  isTme: boolean
): LandingPageOption[] {
  return LANDING_PAGES.filter((option) => {
    if (isTme && option.hideForTme) return false;
    if (!isTme && option.tmeOnly) return false;
    const feature = featureForRoutePath(option.path, isTme);
    // "tickets" is unmapped on purpose - open to everyone.
    if (!feature) return true;
    return canAccessFeature(scopes, feature);
  });
}

/** Display name for a stored default route, falling back to the raw path. */
export function describeDefaultRoute(
  defaultRoute: string | null | undefined,
  isTme: boolean
): string | null {
  if (!defaultRoute) return null;
  const target = routeKey(defaultRoute);
  const match = LANDING_PAGES.find((option) => routeKey(option.path) === target);
  return match ? landingPageLabel(match, isTme) : defaultRoute;
}

/** "/leads/", "leads", "/leads?tab=x" all key as "leads" for matching against sidebar `to`s. */
export function routeKey(path: string): string {
  return path.split("?")[0].split("#")[0].replace(/^\/+/, "").replace(/\/+$/, "");
}

/** The subset of NavigationItemInterface the landing rules need. */
export interface LandingNavItem {
  to: string;
  group?: string;
  hidden?: boolean;
  disabled?: boolean;
}

export interface ResolveLandingPathArgs {
  /** defaultRoute claim from the JWT; undefined/empty = none set. */
  defaultRoute: string | undefined;
  /** The sidebar as the user actually sees it - already scope-gated, with hidden/disabled set. */
  navigation: LandingNavItem[];
  canAccessDashboard: boolean;
  /** True once this page load has already followed the default route. */
  alreadyRedirected: boolean;
}

const NEVER_LAND_ON = new Set(["", "help"]);

/**
 * Where a user standing at "/" should be sent, or null to stay.
 *
 * - The role's default route wins when it is a visible, enabled sidebar item. Users who
 *   can see the Dashboard are only moved once per page load, so the sidebar's Dashboard
 *   link still works for them; users who can't are always moved off "/".
 * - With no usable default, someone without Dashboard access goes to the first enabled
 *   sidebar item - preferring a grouped (work area) page over the ungrouped ones at the top.
 * - Only visible, enabled items are ever chosen. DashboardLayout hard-redirects disabled
 *   pages back to "/", so landing on one would loop.
 */
export function resolveLandingPath({
  defaultRoute,
  navigation,
  canAccessDashboard,
  alreadyRedirected,
}: ResolveLandingPathArgs): string | null {
  const usable = navigation.filter(
    (item) => !item.hidden && !item.disabled && !NEVER_LAND_ON.has(routeKey(item.to))
  );

  const wanted = defaultRoute?.trim();
  if (wanted) {
    const target = routeKey(wanted);
    const match = usable.find((item) => routeKey(item.to) === target);
    if (match && (!canAccessDashboard || !alreadyRedirected)) {
      return wanted.startsWith("/") ? wanted : `/${wanted}`;
    }
  }

  if (canAccessDashboard) return null;

  const fallback = usable.find((item) => item.group) ?? usable[0];
  return fallback ? `/${routeKey(fallback.to)}` : null;
}
